import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { api, authApi } from "../Apis/Api";
import { usePermissions } from "../context/PermissionContext";
import { auth } from "../firebase";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { handleApiResponse } from "../utils/response";
import { toast } from "react-toastify";
import { saveFcmToken } from "../utils/saveFcmToken";
import logo from "../assets/Group 2085662814.png";

// Yup validation schema
const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email address"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 6 characters"),
  rememberMe: Yup.boolean(),
});

const SignInPage = () => {
  const [loading, setLoading] = useState(false);
  const [selfLoading, setSelfLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshAuthStatus, hasPermission } = usePermissions();

  const [passwordVisible, setPasswordVisible] = useState(false);
  const emailInputRef = useRef(null);
  const statusTimeoutRef = useRef(null);
  const [locations, setLocations] = useState({
    latitude: null,
    longitude: null,
  });
  const planLocation = useLocation();
  const planData = planLocation.state?.plan;
  const [locationError, setLocationError] = useState(null);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  useEffect(() => {
    const getLocation = () => {
      if (!navigator.geolocation) {
        setLocationError("Geolocation is not supported by your browser.");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocations({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setLocationError(null);
        },
        (error) => {
          console.error("Error getting location:", error);
          setLocationError(
            "Unable to retrieve your location. Please allow location access."
          );
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    };

    getLocation();
  }, []);

  useEffect(() => {
    emailInputRef.current?.focus();

    const token = localStorage.getItem("access_token");
    const expiryTime = localStorage.getItem("token_expiry");
    const currentTime = Date.now();

    if (token && expiryTime && currentTime < parseInt(expiryTime, 10)) {
      if (hasPermission("view_dashboard")) {
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/home", { replace: true });
      }
    } else {
      localStorage.clear();
    }

    const savedCredentials = localStorage.getItem("rememberedCredentials");
    if (savedCredentials) {
      try {
        const { email, rememberMe } = JSON.parse(savedCredentials);
        setValue("email", email);
        setValue("rememberMe", rememberMe);
      } catch {
        localStorage.removeItem("rememberedCredentials");
      }
    }

    return () => clearTimeout(statusTimeoutRef.current);
  }, [location, navigate, setValue, hasPermission]);

  const handlePlanSelect = async (plan) => {
    try {
      const response = await api.post("/stripe/create-checkout-session", {
        plan_id: plan.plan_id,
      });
      if (response.data.success && response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error("Invalid response from checkout API");
      }
    } catch (error) {
      console.error("Stripe Checkout API Error:", error);
      toast.error(
        error.response?.data?.message || "Failed to initiate checkout"
      );
    }
  };

  // Handle Google Sign-In (Login Only)
  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: "select_account",
      });
      const result = await signInWithPopup(auth, provider);
      const data = result.user;
      const user = {
        name: data.displayName,
        email: data.email,
        photo: data.photoURL,
      };

      const response = await authApi.google_login(
        user.name,
        user.email,
        user.photo,
        location.latitude,
        location.longitude
      );

      if (
        response.status === "error" &&
        response.message.includes("user not found")
      ) {
        throw new Error("User not found. Please sign up first.");
      }

      handleApiResponse(response, "Login Successfully", "POST");

      if (response.data.rememberMe) {
        localStorage.setItem(
          "rememberedCredentials",
          JSON.stringify({ email: user.email, rememberMe: true })
        );
      } else {
        localStorage.removeItem("rememberedCredentials");
      }

      const expiryTime = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
      localStorage.setItem("access_token", response.access_token);
      localStorage.setItem("token_expiry", expiryTime.toString());
      localStorage.setItem("user", JSON.stringify(response.data));
      localStorage.setItem("isExpired", response.data.isExpired);

      await saveFcmToken(response.data);
      if (planData) {
        await handlePlanSelect(planData);
      }
      await refreshAuthStatus();
      if (response.data.is_client_admin) {
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/home", { replace: true });
      }
    } catch (error) {
      localStorage.clear();
      handleApiResponse(
        error,
        error.message ||
          "Google Sign-In Failed. Please sign up if you don't have an account.",
        "POST"
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  // Handle form submission (Email/Password)
  const onSubmit = async (data) => {
    try {
      setSelfLoading(true);
      const response = await authApi.login(data.email, data.password, "self");
      handleApiResponse(response, "Login Successfully", "POST");

      if (data.rememberMe) {
        localStorage.setItem(
          "rememberedCredentials",
          JSON.stringify({ email: data.email, rememberMe: true })
        );
      } else {
        localStorage.removeItem("rememberedCredentials");
      }

      const expiryTime = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
      localStorage.setItem("access_token", response.access_token);
      localStorage.setItem("token_expiry", expiryTime.toString());
      localStorage.setItem("user", JSON.stringify(response.data));
      localStorage.setItem("isExpired", response.data.isExpired);
      localStorage.setItem("country_code", response.data.country_code);

      await saveFcmToken(response.data);
      if (planData) {
        await handlePlanSelect(planData);
      }

      await refreshAuthStatus();

      if (response.data.is_client_admin) {
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/home", { replace: true });
      }
    } catch (error) {
      localStorage.clear();
      handleApiResponse(error, "Failed to Login", "POST");
    } finally {
      setSelfLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          .auth-container {
            min-height: 100vh;
            background: black;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1rem;
            position: relative;
            overflow: hidden;
          }

          /* Black gradient overlay */
          .auth-container::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 60%;
            background: linear-gradient(
              to bottom,
              rgba(0, 0, 0, 0.9) 0%,
              rgba(0, 0, 0, 0.7) 50%,
              rgba(0, 0, 0, 0) 100%
            );
            z-index: 1;
            pointer-events: none;
          }

          .background-video {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            z-index: 0;
          }

          .auth-section {
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 1rem;
            overflow: hidden;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
            max-width: 450px;
            width: 100%;
            min-height: 550px;
            position: relative;
            z-index: 2;
            background: white;
            zoom:70%;
          }

          .auth-right {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 2rem;
            background: white;
            overflow-y: auto;
            min-height: 100%;
            width: 100%;
          }

          .auth-right::-webkit-scrollbar {
            width: 6px;
          }

          .auth-right::-webkit-scrollbar-track {
            background: #f1f1f1;
          }

          .auth-right::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }

          .auth-right::-webkit-scrollbar-thumb:hover {
            background: #555;
          }

          .auth-form {
            width: 100%;
            max-height: 100vh;
            background: white;
          }

          .auth-form h3 {
            font-size: 1.5rem;
            font-weight: 700;
            color: #1a202c;
            margin: 0 0 0.25rem;
            text-align: center;
          }

          .auth-form > p {
            color: #718096;
            margin: 0 0 1.5rem;
            text-align: center;
            font-size: 1rem;
          }

          .auth-form .form-group {
            position: relative;
            margin-bottom: 1rem;
          }

          .auth-form label {
            display: block;
            font-size: 0.8rem;
            color: #4a5568;
            margin-bottom: 0.35rem;
            text-align: left;
            font-weight: 500;
          }

          .auth-form input, .auth-form .PhoneInputInput {
            width: 100%;
            padding: 0.8rem 1rem 0.8rem 3rem;
            border: 1px solid #e2e8f0;
            border-radius: 0.5rem;
            transition: all 0.3s;
            font-size: 0.95rem;
            box-sizing: border-box;
            background: #f7fafc;
          }

          .auth-form input:focus, .auth-form .PhoneInputInput:focus {
            outline: none;
            border-color: #E63C23;
            background: white;
            box-shadow: 0 0 0 3px rgba(230, 60, 35, 0.1);
          }

          .auth-form input.is-invalid, .auth-form .PhoneInputInput.is-invalid {
            border-color: #e53e3e;
          }

          .auth-form .input-icon-wrapper {
            position: relative;
          }

          .auth-form .icon {
            position: absolute;
            left: 0.9rem;
            top: 50%;
            transform: translateY(-50%);
            color: #a0aec0;
            font-size: 1.2rem;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
            z-index: 2;
          }

          .auth-form .password-toggle {
            position: absolute;
            right: 0.9rem;
            top: 50%;
            transform: translateY(-50%);
            color: #a0aec0;
            cursor: pointer;
            font-size: 1.2rem;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 5;
          }

          .auth-form .password-toggle:hover {
            color: #E63C23;
          }

          .auth-form .password-hint {
            font-size: 0.7rem;
            color: #e53e3e;
            margin-top: 0.35rem;
            text-align: left;
          }

          .auth-form .checkbox-group {
            display: flex;
            align-items: flex-start;
            margin-bottom: 1rem;
          }

          .auth-form .checkbox-group .agree-terms {
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .auth-form .checkbox-group input[type="checkbox"] {
            width: 18px;
            height: 18px;
            margin: 0;
            padding: 0;
            flex-shrink: 0;
            cursor: pointer;
            border: 2px solid #e2e8f0;
            border-radius: 4px;
            appearance: none;
            background: #fff;
            position: relative;
            margin-top: 0.1rem;
          }

          .auth-form .checkbox-group input[type="checkbox"]:checked {
            background: #E63C23;
            border-color: #E63C23;
          }

          .auth-form .checkbox-group input[type="checkbox"]:checked::after {
            content: '';
            position: absolute;
            left: 5px;
            top: 2px;
            width: 5px;
            height: 10px;
            border: solid white;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
          }

          .auth-form .checkbox-group label {
            font-size: 0.8rem;
            color: #718096;
            line-height: 1.3;
            cursor: pointer;
          }

          .auth-form .submit-button {
            margin-top: 1rem;
          }

          .auth-form button[type="submit"] {
            width: 100%;
            padding: 0.75rem;
            background: #E63C23;
            color: white;
            border: none;
            border-radius: 0.5rem;
            font-size: 0.95rem;
            font-weight: 600;
            transition: background 0.3s;
            cursor: pointer;
          }

          .auth-form button[type="submit"]:hover {
            background: #d1341b;
          }

          .auth-form button[type="submit"]:disabled {
            background: #cbd5e0;
            cursor: not-allowed;
          }

          .auth-form button.social-btn {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.75rem;
            background: white;
            color: #4a5568;
            border: 1px solid #e2e8f0;
            margin-bottom: 1rem;
            padding: 0.75rem;
            border-radius: 0.5rem;
            font-weight: 500;
            transition: all 0.3s;
            font-size: 0.9rem;
          }

          .auth-form button.social-btn:hover {
            background: #f7fafc;
            border-color: #cbd5e0;
          }

          .auth-form .register-link {
            text-align: center;
            margin-top: 1rem;
            font-size: 0.8rem;
            color: #718096;
          }

          .auth-form .register-link a {
            color: #E63C23;
            text-decoration: none;
            font-weight: 600;
            cursor: pointer;
            margin-bottom: 1rem;
          }

          .auth-form .register-link a:hover {
            text-decoration: underline;
          }

          .auth-logo {
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  z-index: 10;
  background-color: white;
  padding: 0.5rem;
  border-radius: 0.5rem;
}

.auth-logo img {
  height: 30px;
  width: auto;
  object-fit: contain;
}

          .forgot-password-link {
            text-align: right;
            margin-bottom: 1rem;
          }

          .forgot-password-link a {
            color: #E63C23;
            text-decoration: none;
            font-size: 0.8rem;
            font-weight: 500;
          }

          .forgot-password-link a:hover {
            text-decoration: underline;
          }

          /* ========== RESPONSIVE STYLES ========== */

          /* Large Desktop (1200px and above) */
          @media (min-width: 1200px) {
            .auth-section {
              max-width: 500px;
              min-height: 600px;
            }
            
            .auth-right {
              padding: 2.5rem;
            }
          }

          /* Desktop (992px to 1199px) */
          @media (min-width: 992px) and (max-width: 1199px) {
            .auth-section {
              max-width: 480px;
              min-height: 580px;
            }
          }

          /* Tablet Landscape (768px to 991px) */
          @media (min-width: 768px) and (max-width: 991px) {
            .auth-section {
              max-width: 450px;
              min-height: 550px;
            }
            
            .auth-right {
              padding: 1.5rem;
            }
            
            .auth-form > p {
              font-size: 0.9rem;
            }
          }

          /* Tablet Portrait (576px to 767px) */
          @media (min-width: 576px) and (max-width: 767px) {
            .auth-container {
              padding: 0.5rem;
            }
            
            .auth-section {
              max-width: 420px;
              min-height: 500px;
              border-radius: 0.75rem;
            }
            
            .auth-right {
              padding: 1.5rem;
            }
            
            .auth-form h3 {
              font-size: 1.3rem;
            }
            
            .auth-form > p {
              font-size: 0.85rem;
              margin-bottom: 1.25rem;
            }
            
            .auth-logo {
              top: 1rem;
              left: 1rem;
            }
            
            .auth-logo img {
              height: 40px;
            }
          }

          /* Mobile Landscape (480px to 575px) */
          @media (min-width: 480px) and (max-width: 575px) {
            .auth-container {
              padding: 0.5rem;
            }
            
            .auth-section {
              max-width: 400px;
              min-height: 480px;
              border-radius: 0.75rem;
            }
            
            .auth-right {
              padding: 1.25rem;
            }
            
            .auth-form h3 {
              font-size: 1.25rem;
            }
            
            .auth-form > p {
              font-size: 0.8rem;
              margin-bottom: 1rem;
            }
            
            .auth-form input, .auth-form .PhoneInputInput {
              padding: 0.7rem 1rem 0.7rem 2.8rem;
              font-size: 0.9rem;
            }
            
            .auth-form .icon {
              left: 0.8rem;
              font-size: 1.1rem;
            }
            
            .auth-form .password-toggle {
              right: 0.8rem;
              font-size: 1.1rem;
            }
            
            .auth-logo {
              top: 0.8rem;
              left: 0.8rem;
            }
            
            .auth-logo img {
              height: 35px;
            }
          }

           /* Mobile Portrait (320px to 479px) */
          @media (max-width: 479px) {
            .auth-container {
              padding: 0.25rem;
              min-height: 100vh;
              align-items: center;
              display: flex;
              justify-content: center;
            }
            
            .auth-section {
              max-width: 100%;
              min-height: auto;
              margin: auto;
              border-radius: 0.5rem;
              box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
            }
            
            .auth-right {
              padding: 1.5rem 1rem;
            }
            
            .auth-form h3 {
              font-size: 1.2rem;
              margin-bottom: 0.5rem;
            }
            
            .auth-form > p {
              font-size: 0.75rem;
              margin-bottom: 1rem;
            }
            
            .auth-form input, .auth-form .PhoneInputInput {
              padding: 0.65rem 0.9rem 0.65rem 2.5rem;
              font-size: 0.85rem;
            }
            
            .auth-form .icon {
              left: 0.7rem;
              font-size: 1rem;
            }
            
            .auth-form .password-toggle {
              right: 0.7rem;
              font-size: 1rem;
            }
            
            .auth-form label {
              font-size: 0.75rem;
            }
            
            .auth-form .checkbox-group label {
              font-size: 0.75rem;
            }
            
            .auth-form button.social-btn {
              font-size: 0.8rem;
              padding: 0.65rem;
            }
            
            .auth-form button[type="submit"] {
              padding: 0.65rem;
              font-size: 0.9rem;
            }
            
            .auth-form .register-link {
              font-size: 0.75rem;
            }
            
            .auth-logo {
              top: 0.5rem;
              left: 0.5rem;
            }
            
            .auth-logo img {
              height: 30px;
            }
            
            .forgot-password-link a {
              font-size: 0.75rem;
            }
          }

          /* Extra Small Mobile (max-width: 319px) */
          @media (max-width: 319px) {
            .auth-container {
              padding: 0.1rem;
            }
            
            .auth-right {
              padding: 1rem 0.75rem;
            }
            
            .auth-form h3 {
              font-size: 1.1rem;
            }
            
            .auth-form input, .auth-form .PhoneInputInput {
              padding: 0.6rem 0.8rem 0.6rem 2.3rem;
              font-size: 0.8rem;
            }
            
            .auth-form .icon {
              left: 0.6rem;
              font-size: 0.9rem;
            }
            
            .auth-form button.social-btn,
            .auth-form button[type="submit"] {
              padding: 0.6rem;
              font-size: 0.8rem;
            }
          }

          /* Landscape orientation for mobile */
          @media (max-height: 600px) and (max-width: 767px) {
            .auth-container {
              align-items: flex-start;
              padding-top: 1rem;
            }
            
            .auth-section {
              max-height: 95vh;
              overflow-y: auto;
              margin: 0.5rem 0;
            }
            
            .auth-right {
              padding: 1rem;
            }
          }

          /* High DPI screens */
          @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
            .auth-logo img {
              image-rendering: -webkit-optimize-contrast;
            }
          }
        `}
      </style>

      <div className="auth-container">
        <div className="auth-logo">
          <img src={logo} alt="Logo" />
        </div>

        <video
          className="background-video"
          src="assets/video/auth-video-v2.mp4"
          autoPlay
          // loop
          muted
        />

        <section className="auth-section">
          <div className="auth-right">
            <div className="auth-form">
              <p className="fw-bolder display-5 h3">Sign In</p>
              <p>Enter your credentials to access your account</p>

              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <div className="input-icon-wrapper">
                    <span className="icon">
                      <Icon icon="mage:email" />
                    </span>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="email"
                          id="email"
                          {...field}
                          placeholder="Email"
                          ref={emailInputRef}
                          className={errors.email ? "is-invalid" : ""}
                          aria-describedby="email-error"
                        />
                      )}
                    />
                  </div>
                  {errors.email && (
                    <p id="email-error" className="password-hint">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="input-icon-wrapper">
                    <span className="icon">
                      <Icon icon="solar:lock-password-outline" />
                    </span>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <input
                          type={passwordVisible ? "text" : "password"}
                          id="password"
                          {...field}
                          placeholder="Password"
                          className={errors.password ? "is-invalid" : ""}
                          aria-describedby="password-error"
                        />
                      )}
                    />
                    <span
                      className="password-toggle"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      title={
                        passwordVisible ? "Hide password" : "Show password"
                      }
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        setPasswordVisible(!passwordVisible)
                      }
                    >
                      <Icon
                        icon={
                          passwordVisible ? "ri:eye-off-line" : "ri:eye-line"
                        }
                      />
                    </span>
                  </div>
                  {errors.password && (
                    <p id="password-error" className="password-hint">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div className="forgot-password-link">
                  <Link to="/forgot-password">Forgot Password?</Link>
                </div>

                <div className="checkbox-group">
                  <div className="d-flex justify-content-center align-items-center gap-2">
                    <Controller
                      name="rememberMe"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          id="rememberMe"
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                    <label htmlFor="rememberMe" className="mb-0">
                      Remember me
                    </label>
                  </div>
                </div>

                <div className="social-signup mb-3">
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={googleLoading}
                    className="social-btn"
                  >
                    {googleLoading ? (
                      <>
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Signing in...
                      </>
                    ) : (
                      <>
                        <Icon icon="logos:google-icon" width="20" height="20" />
                        Continue with Google
                      </>
                    )}
                  </button>
                </div>

                <div className="submit-button">
                  <button
                    type="submit"
                    disabled={selfLoading}
                    className="text-center"
                  >
                    {selfLoading ? "Please wait..." : "Sign In"}
                  </button>
                </div>

                <p className="register-link">
                  Don't have an account? <Link to="/sign-up">Sign Up</Link>
                </p>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SignInPage;
